import { createSlice, createAsyncThunk, Draft } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type Item = {
  id: string;
};

export type ItemMetadata = {
  total: number;
  page: number;
  limit: number;
};

export type ItemState<T> = {
  ids: string[];
  entities: Record<string, T>;
  isLoading: boolean;
  isLoadingCreate: boolean;
  isLoadingDelete: boolean;
  metadata: ItemMetadata;
};

export type RestSliceOptions = {
  extraReducers: (builder: any) => void;
};

const replaceParams = (endpoint: string, params: Record<string, string>) => {
  return endpoint.replace(/:(\w+)/g, (match, p1) => params[p1] || match);
};

export const createRestSlice = <T extends Item>(name: string, endpoint: string, options?: RestSliceOptions) => {
  const get = createAsyncThunk(`${name}/get`, async (query: Partial<Record<string, string | unknown>> = {}) => {
    const directParams = ["page", "limit", "includeAgentCount"];
    const queryString = Object.entries(query)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => {
        if (directParams.includes(key)) {
          return `${key}=${value}`;
        }

        if (typeof value === "object" && value !== null) {
          return Object.entries(value)
            .map(([innerKey, innerValue]) => {
              return `filters[${key}][${innerKey}]=${innerValue}`;
            })
            .join("&");
        }

        return `filters[${key}]=${value}`;
      })
      .join("&");
    const { data, metadata } = await makeRequest<{ data: T[]; metadata: ItemMetadata }>(
      `${replaceParams(endpoint, query as Record<string, string>)}?${queryString}`,
    );

    return {
      data,
      metadata,
    };
  });

  const create = createAsyncThunk(`${name}/create`, async (item: Partial<T>) => {
    const { data } = await makeRequest<{ data: T }>(replaceParams(endpoint, item as Record<string, string>), {
      method: "POST",
      body: item,
    });

    return data;
  });

  const createStream = createAsyncThunk(`${name}/createStream`, async (item: Partial<T>) => {
    const { data } = await makeRequest<{ data: T }>(`${replaceParams(endpoint, item as Record<string, string>)}/stream`, {
      method: "POST",
      body: item,
    });

    return data;
  });

  const getById = createAsyncThunk(`${name}/getById`, async (item: Partial<T>) => {
    const { data } = await makeRequest<{ data: T }>(`${replaceParams(endpoint, item as Record<string, string>)}/${item.id}`);

    return data;
  });

  const updateById = createAsyncThunk(`${name}/updateById`, async (item: Partial<T>) => {
    const id = item.id;

    delete item.id;

    const { data } = await makeRequest<{ data: T }>(`${replaceParams(endpoint, item as Record<string, string>)}/${id}`, {
      method: "PATCH",
      body: item,
    });

    return data;
  });

  const updateLocalById = createAsyncThunk(`${name}/updateLocalById`, async (item: Partial<T>) => {
    return item as T;
  });

  const deleteById = createAsyncThunk(`${name}/deleteById`, async (item: Partial<T>) => {
    await makeRequest(`${replaceParams(endpoint, item as Record<string, string>)}/${item.id}`, {
      method: "DELETE",
    });

    return item.id;
  });

  const initialState: ItemState<T> = {
    ids: [],
    entities: {},
    isLoading: false,
    isLoadingCreate: false,
    isLoadingDelete: false,
    metadata: {
      total: 0,
      page: 1,
      limit: 100,
    },
  };

  const slice = createSlice({
    name: "user",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      if (options?.extraReducers) {
        options.extraReducers(builder);
      }

      // Fulfilled actions
      builder.addCase(get.fulfilled, (state, action) => {
        state.isLoading = false;
        state.metadata = action.payload.metadata;
        state.ids = action.payload.data.map((item: T) => item.id);
        state.entities = action.payload.data.reduce(
          (acc: Record<string, Draft<T>>, item: T) => {
            acc[item.id] = item as Draft<T>;

            return acc;
          },
          {} as Record<string, Draft<T>>,
        );
      });

      builder.addCase(create.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingCreate = false;
        state.ids.push(action.payload.id);
        state.entities[action.payload.id] = action.payload as Draft<T>;
      });

      builder.addCase(createStream.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingCreate = false;
        state.ids.push(action.payload.id);
        state.entities[action.payload.id] = action.payload as Draft<T>;
      });

      builder.addCase(getById.fulfilled, (state, action) => {
        state.isLoading = false;

        if (!state.ids.includes(action.payload.id)) {
          state.ids.push(action.payload.id);
        }

        state.entities[action.payload.id] = action.payload as Draft<T>;
      });

      builder.addCase(updateById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entities[action.payload.id] = action.payload as Draft<T>;
      });

      builder.addCase(updateLocalById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entities[action.payload.id] = action.payload as Draft<T>;
      });

      builder.addCase(deleteById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingDelete = false;
        state.ids = state.ids.filter((id) => id !== action.payload);
        delete state.entities[action.payload as string];
      });

      // Pending actions
      builder.addCase(create.pending, (state, action) => {
        state.isLoadingCreate = true;
      });

      builder.addCase(createStream.pending, (state, action) => {
        state.isLoadingCreate = true;
      });

      builder.addCase(deleteById.pending, (state, action) => {
        state.isLoadingDelete = true;
      });

      builder.addCase(get.pending, (state, action) => {
        state.isLoading = true;
      });

      builder.addCase(getById.pending, (state, action) => {
        state.isLoading = true;
      });

      builder.addCase(updateById.pending, (state, action) => {
        state.isLoading = true;
      });

      // Rejected actions
      builder.addMatcher(
        (action) => action.type.startsWith(`${name}/`) && action.type.endsWith("/rejected"),
        (state) => {
          state.isLoading = false;
          state.isLoadingCreate = false;
          state.isLoadingDelete = false;
        },
      );
    },
  });

  return {
    actions: {
      ...slice.actions,
      get,
      create,
      createStream,
      getById,
      updateById,
      updateLocalById,
      deleteById,
    },
    reducer: slice.reducer,
  };
};
